/**
 * LUMINA · LiuyaoPage.jsx  v1.0
 * 六爻占卜页 — 梅花易数时间起卦
 * 6次点击摇卦仪式 → 毫秒时间戳种子 → 本卦/动爻/变卦 → AI 解读
 */

import { useState, useRef, useCallback, useEffect } from "react";
import useSEO from "../utils/useSEO";
import { track } from "../utils/track";

// ── 八卦图符（Unicode Unicode Braille-like，改用文字符号） ──────────────────
const GUA_SYMBOL = {
  乾: "☰", 兑: "☱", 离: "☲", 震: "☳",
  巽: "☴", 坎: "☵", 艮: "☶", 坤: "☷",
};

const SIX_RELATIVE_COLOR = {
  兄弟: "#6b7280", 子孙: "#10b981", 妻财: "#f59e0b",
  父母: "#8b5cf6", 官鬼: "#ef4444",
};

const SPIRIT_COLOR = {
  青龙: "#10b981", 勾陈: "#6b7280", 朱雀: "#ef4444",
  白虎: "#f9fafb", 腾蛇: "#8b5cf6", 玄武: "#1e3a5f",
};

const WANGXIU_BADGE = {
  旺: { bg: "#fef3c7", text: "#92400e", label: "旺" },
  相: { bg: "#d1fae5", text: "#065f46", label: "相" },
  休: { bg: "#f3f4f6", text: "#6b7280", label: "休" },
  囚: { bg: "#fee2e2", text: "#991b1b", label: "囚" },
  死: { bg: "#1f2937", text: "#6B7280", label: "死" },
  平: { bg: "#f3f4f6", text: "#6b7280", label: "平" },
};

// ── 爻线 SVG ──────────────────────────────────────────────────────────────────
function YaoLine({ line, idx }) {
  const isMoving = line.is_moving;
  const relColor = SIX_RELATIVE_COLOR[line.six_rel] || "#6B7280";
  const spiritColor = SPIRIT_COLOR[line.spirit] || "#6B7280";
  const wx = WANGXIU_BADGE[line.wangxiu] || WANGXIU_BADGE["平"];

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "6px 0",
        borderBottom: "1px solid rgba(0,0,0,0.06)",
        background: isMoving ? "rgba(251,191,36,0.08)" : "transparent",
        borderRadius: 4,
        paddingLeft: 6,
        paddingRight: 6,
      }}
    >
      {/* 爻位编号 */}
      <span style={{ fontSize: 11, color: "#6B7280", width: 28, flexShrink: 0 }}>
        {["初", "二", "三", "四", "五", "上"][idx]}爻
      </span>

      {/* 爻线图形 */}
      <div style={{ display: "flex", gap: 3, alignItems: "center", width: 48 }}>
        {[0, 1].map((i) => (
          <div
            key={i}
            style={{
              height: 4,
              flex: 1,
              borderRadius: 2,
              background: isMoving ? "#fbbf24" : "#D4AF37",
              boxShadow: isMoving ? "0 0 6px #fbbf24" : "none",
            }}
          />
        ))}
        {isMoving && (
          <span
            style={{
              fontSize: 10,
              color: "#fbbf24",
              fontWeight: 700,
              marginLeft: 2,
            }}
          >
            动
          </span>
        )}
      </div>

      {/* 地支 + 五行 */}
      <span style={{ color: "#064E3B", fontSize: 13, width: 24 }}>
        {line.branch}
      </span>
      <span style={{ fontSize: 11, color: "#6B7280", width: 16 }}>
        {line.element}
      </span>

      {/* 六亲 */}
      <span
        style={{
          fontSize: 11,
          color: relColor,
          width: 32,
          fontWeight: 600,
        }}
      >
        {line.six_rel}
      </span>

      {/* 六神 */}
      <span
        style={{
          fontSize: 11,
          color: spiritColor,
          width: 32,
        }}
      >
        {line.spirit}
      </span>

      {/* 旺相休囚死 */}
      <span
        style={{
          fontSize: 10,
          background: wx.bg,
          color: wx.text,
          borderRadius: 4,
          padding: "1px 5px",
          fontWeight: 600,
        }}
      >
        {wx.label}
      </span>
    </div>
  );
}

// ── 卦象展示卡片 ────────────────────────────────────────────────────────────────
function HexagramCard({ title, titleEn, meaning, lines, highlight, modernTranslation }) {
  const upperName = title?.slice(0, 1) || "";
  const sym1 = GUA_SYMBOL[upperName] || "☰";
  const lowerName = title?.slice(-1) || "";
  const sym2 = GUA_SYMBOL[lowerName] || "☷";

  return (
    <div
      style={{
        background: "rgba(0,0,0,0.04)",
        borderRadius: 12,
        padding: "16px 18px",
        border: highlight
          ? "1px solid rgba(251,191,36,0.4)"
          : "1px solid rgba(0,0,0,0.08)",
        flex: 1,
        minWidth: 0,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
        <span style={{ fontSize: 28, opacity: 0.8 }}>
          {sym1}{sym2}
        </span>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: highlight ? "#b45309" : "#8B6914" }}>
            {title}
          </div>
          <div style={{ fontSize: 11, color: "#6B7280" }}>{titleEn}</div>
        </div>
      </div>
      <p style={{ fontSize: 12, color: "#4B5563", lineHeight: 1.6, margin: 0 }}>
        {meaning}
      </p>
      {modernTranslation && (
        <p style={{
          fontSize: 12, color: "#064E3B", lineHeight: 1.7, margin: "8px 0 0",
          padding: "8px 10px", background: "rgba(6,78,59,0.04)", borderRadius: 6,
          border: "1px solid rgba(6,78,59,0.1)",
        }}>
          💬 {modernTranslation}
        </p>
      )}
    </div>
  );
}

// ── 主页面 ────────────────────────────────────────────────────────────────────
export default function LiuyaoPage({ user, apiBase = "/api", lang = "zh" }) {
  const [step, setStep] = useState("question"); // question | ritual | result | loading
  const [question, setQuestion] = useState("");
  const [shakeCount, setShakeCount] = useState(0);
  const [timestamps, setTimestamps] = useState([]);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [pdfLoading, setPdfLoading] = useState(false);
  const [rippleKey, setRippleKey] = useState(0);
  // credit system for L0 users
  const [credits, setCredits] = useState(null);   // null=loading, -1=unlimited, n=count
  const [buyingCredit, setBuyingCredit] = useState(false);
  // 大运流年联动
  const [cycle, setCycle] = useState(null);  // {has_report, dayun_gz, year_score, top_deity, ...}
  const [guaTranslations, setGuaTranslations] = useState({});  // 64卦LLM现代翻译

  const totalShakes = 6;

  // ── 页面曝光 ──────────────────────────────────────────────────────────────
  useEffect(() => {
    track("liuyao_page_view", { lang, authed: !!user });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── SEO ────────────────────────────────────────────────────────────────────
  useSEO({
    title: "六爻占卜 · LUMINA — 梅花易数 AI 解读",
    description:
      "输入当下心事，六次摇卦，揭示吉凶悔吝。基于梅花易数时间起卦，结合 AI 深度解读，助你洞察决策时机。",
    canonical: "https://lumarcai.com/liuyao",
    ogImage: "https://lumarcai.com/og-liuyao.png",
    jsonLdId: "ld-json-liuyao",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Service",
      name: "六爻占卜 · LUMINA",
      description:
        "梅花易数时间起卦结合 Claude AI 解读，六爻占卜助你洞察事业、感情、决策的吉凶时机。",
      url: "https://lumarcai.com/liuyao",
      provider: {
        "@type": "Organization",
        name: "LUMINA Energy",
        url: "https://lumarcai.com",
      },
      serviceType: "Divination / Chinese Astrology",
      offers: {
        "@type": "Offer",
        price: "29",
        priceCurrency: "USD",
        description: "单次六爻占卜（含 AI 深度解读 + PDF 报告）",
      },
      inLanguage: ["zh-Hans", "en"],
      areaServed: "Worldwide",
    },
  });

  // Fetch credits on mount (if user logged in)
  useEffect(() => {
    if (!user) { setCredits(0); return; }
    const token = localStorage.getItem("lumina_token");
    if (!token) { setCredits(0); return; }
    fetch(`${apiBase}/audit/liuyao/credits`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((d) => setCredits(d.unlimited ? -1 : (d.credits ?? 0)))
      .catch(() => setCredits(0));
    // 大运流年数据（静默失败）
    fetch(`${apiBase}/audit/current-cycle`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.ok ? r.json() : null)
      .then((d) => d?.has_report ? setCycle(d) : null)
      .catch(() => {});
  }, [user, apiBase]);

  // Buy a credit ($29 one-time)
  const handleBuyCredit = async () => {
    if (!user) { alert(lang === "zh" ? "请先登录" : "Please log in first"); return; }
    track("liuyao_buy_click", { lang, credits });
    setBuyingCredit(true);
    try {
      const token = localStorage.getItem("lumina_token");
      const res = await fetch(`${apiBase}/audit/liuyao/checkout`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        alert(data.detail || (lang === "zh" ? "支付链接获取失败" : "Failed to get payment link"));
      }
    } catch {
      alert(lang === "zh" ? "网络错误，请重试" : "Network error, please retry");
    } finally {
      setBuyingCredit(false);
    }
  };

  const handleShake = useCallback(() => {
    if (shakeCount >= totalShakes) return;
    // 首次摇卦埋点
    if (shakeCount === 0) track("liuyao_cast_begin", { lang, question_len: question.length });
    const ts = Date.now();
    const newTs = [...timestamps, ts];
    setTimestamps(newTs);
    setShakeCount((c) => c + 1);
    setRippleKey((k) => k + 1);

    if (newTs.length === totalShakes) {
      setTimeout(() => submitCast(newTs), 600);
    }
  }, [shakeCount, timestamps]);

  const submitCast = async (ts) => {
    setStep("loading");
    setError("");
    try {
      const token = localStorage.getItem("lumina_token");
      const res = await fetch(`${apiBase}/audit/liuyao`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          question,
          seed_timestamps: ts,
          lang,
        }),
      });

      if (res.status === 401 || res.status === 403) {
        setError(lang === "zh"
          ? "请先登录后使用六爻占卜。"
          : "Please log in to use Liuyao divination.");
        setStep("question");
        return;
      }
      if (res.status === 402) {
        setError(lang === "zh"
          ? "占卜次数不足，请购买单次占卜（$29）或升级至 L1。"
          : "No credits remaining. Purchase a single reading ($29) or upgrade to L1.");
        setStep("question");
        // refresh credits count
        setCredits(0);
        return;
      }

      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "起卦失败");
      setResult(data);
      setGuaTranslations(data.gua_translations || {});
      setStep("result");
      track("liuyao_cast_done", { lang, hexagram: data.gua_name || data.hexagram });
    } catch (e) {
      setError(e.message || "网络错误，请重试");
      setStep("ritual");
    }
  };

  const reset = () => {
    setStep("question");
    setQuestion("");
    setShakeCount(0);
    setTimestamps([]);
    setResult(null);
    setError("");
  };

  const downloadPdf = async () => {
    if (!result || pdfLoading) return;
    track("liuyao_pdf_dl", { lang });
    setPdfLoading(true);
    try {
      const token = localStorage.getItem("lumina_token");
      const res = await fetch(`${apiBase}/audit/liuyao/pdf`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ result, lang, cycle_data: cycle || null }),
      });
      if (!res.ok) throw new Error("PDF 生成失败");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const q = (result.question || "liuyao").slice(0, 20).replace(/\s/g, "_");
      a.download = `liuyao_${q}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      alert("PDF 下载失败，请稍后重试");
    } finally {
      setPdfLoading(false);
    }
  };

  // ── 问题输入界面 ────────────────────────────────────────────────────────────
  if (step === "question") {
    return (
      <div style={styles.page}>
        <div style={styles.container}>
          {/* 标题 */}
          <div style={{ textAlign: "center", marginBottom: 32 }}>
            <div style={{ fontSize: 36, marginBottom: 8 }}>☯</div>
            <h1 style={{ fontSize: 22, fontWeight: 700, color: "#064E3B", margin: 0 }}>
              六爻占卜
            </h1>
            <p style={{ color: "#6B7280", fontSize: 13, marginTop: 6 }}>
              梅花易数 · 时间起卦 · AI 解读
            </p>
          </div>

          {/* 次数/权限状态栏 */}
          {credits !== null && (
            <div style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              background: credits === -1
                ? "rgba(16,185,129,0.10)"
                : credits > 0
                  ? "rgba(251,191,36,0.10)"
                  : "rgba(239,68,68,0.10)",
              border: `1px solid ${credits === -1 ? "rgba(16,185,129,0.3)" : credits > 0 ? "rgba(251,191,36,0.3)" : "rgba(239,68,68,0.3)"}`,
              borderRadius: 10,
              padding: "10px 14px",
              marginBottom: 20,
              gap: 12,
            }}>
              <span style={{ fontSize: 13, color: "#4B5563" }}>
                {credits === -1
                  ? (lang === "zh" ? "✦ L1+ 无限占卜" : "✦ L1+ Unlimited readings")
                  : credits > 0
                    ? (lang === "zh" ? `剩余次数：${credits} 次` : `Credits: ${credits} remaining`)
                    : (lang === "zh" ? "暂无占卜次数" : "No credits remaining")}
              </span>
              {credits !== -1 && (
                <button
                  onClick={handleBuyCredit}
                  disabled={buyingCredit}
                  style={{
                    fontSize: 12,
                    padding: "5px 12px",
                    borderRadius: 6,
                    border: "none",
                    background: "linear-gradient(135deg,#D4AF37,#8B6914)",
                    color: "#fff",
                    cursor: buyingCredit ? "wait" : "pointer",
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                  }}
                >
                  {buyingCredit
                    ? (lang === "zh" ? "跳转中…" : "Redirecting…")
                    : (lang === "zh" ? "购买单次 $29" : "Buy 1 reading $29")}
                </button>
              )}
            </div>
          )}

          {/* 说明 */}
          <div style={styles.infoBox}>
            <p style={{ margin: 0, fontSize: 13, color: "#4B5563", lineHeight: 1.7 }}>
              请于心中专注一事，以简洁语句写出您的占问。
              然后完成六次摇卦动作，系统将根据您的意念与时间起卦。
            </p>
          </div>

          {/* 问题输入 */}
          <div style={{ marginBottom: 20 }}>
            <label style={{ fontSize: 13, color: "#6B7280", display: "block", marginBottom: 6 }}>
              您的占问
            </label>
            <textarea
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="例如：这次事业合作是否适合推进？"
              maxLength={200}
              rows={3}
              style={styles.textarea}
            />
            <div style={{ fontSize: 11, color: "#6b7280", textAlign: "right", marginTop: 4 }}>
              {question.length}/200
            </div>
          </div>

          {error && (
            <div style={styles.errorBox}>{error}</div>
          )}

          <button
            onClick={() => {
              if (question.trim().length < 2) {
                setError(lang === "zh" ? "请输入至少2个字的占问" : "Please enter at least 2 characters");
                return;
              }
              if (credits === 0) {
                setError(lang === "zh" ? "请先购买占卜次数或升级 L1" : "Please purchase a credit or upgrade to L1");
                return;
              }
              setError("");
              setStep("calm");
              // 3秒后自动进入摇卦
              setTimeout(() => setStep("ritual"), 3000);
            }}
            disabled={question.trim().length < 2 || credits === 0}
            style={{
              ...styles.btnPrimary,
              opacity: (question.trim().length < 2 || credits === 0) ? 0.5 : 1,
            }}
          >
            {credits === 0
              ? (lang === "zh" ? "购买后方可起卦" : "Purchase to begin")
              : (lang === "zh" ? "入定起卦 →" : "Begin Divination →")}
          </button>

          <p style={{ textAlign: "center", fontSize: 11, color: "#6b7280", marginTop: 16 }}>
            需 L1 订阅 · 每次占卜独立解读
          </p>
        </div>
      </div>
    );
  }

  // ── 静心引导 (Phase 120) ────────────────────────────────────────────────────
  if (step === "calm") {
    return (
      <div style={styles.page}>
        <div style={{ ...styles.container, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "60vh" }}>
          <div style={{ fontSize: 48, marginBottom: 24, animation: "pulse 2s ease-in-out infinite" }}>☯</div>
          <h2 style={{ fontSize: 20, fontWeight: 600, color: "#064E3B", margin: "0 0 12px 0" }}>
            静心凝神
          </h2>
          <p style={{ fontSize: 15, color: "#4B5563", textAlign: "center", lineHeight: 1.8, maxWidth: 320, margin: "0 0 6px 0" }}>
            放慢呼吸，专注所问之事。
          </p>
          <p style={{ fontSize: 13, color: "#9CA3AF", textAlign: "center", margin: 0 }}>
            卦象即将显现...
          </p>
          <style>{`
            @keyframes pulse {
              0%, 100% { opacity: 0.4; transform: scale(1); }
              50% { opacity: 1; transform: scale(1.08); }
            }
          `}</style>
        </div>
      </div>
    );
  }

  // ── 摇卦仪式界面 ────────────────────────────────────────────────────────────
  if (step === "ritual") {
    const progress = shakeCount / totalShakes;
    return (
      <div style={styles.page}>
        <div style={styles.container}>
          <div style={{ textAlign: "center", marginBottom: 24 }}>
            <p style={{ color: "#6B7280", fontSize: 13, marginBottom: 4 }}>占问</p>
            <p style={{
              fontSize: 15,
              color: "#1F2937",
              fontStyle: "italic",
              borderLeft: "2px solid #064E3B",
              paddingLeft: 12,
              textAlign: "left",
            }}>
              {question}
            </p>
          </div>

          {/* 进度弧 */}
          <div style={{ textAlign: "center", marginBottom: 24 }}>
            <svg width={120} height={120} viewBox="0 0 120 120">
              <circle cx={60} cy={60} r={50} fill="none" stroke="rgba(0,0,0,0.06)" strokeWidth={6} />
              <circle
                cx={60} cy={60} r={50}
                fill="none"
                stroke={shakeCount >= totalShakes ? "#fbbf24" : "#D4AF37"}
                strokeWidth={6}
                strokeDasharray={`${2 * Math.PI * 50}`}
                strokeDashoffset={`${2 * Math.PI * 50 * (1 - progress)}`}
                strokeLinecap="round"
                transform="rotate(-90 60 60)"
                style={{ transition: "stroke-dashoffset 0.4s ease" }}
              />
              <text x={60} y={56} textAnchor="middle" fill="#064E3B" fontSize={22} fontWeight={700}>
                {shakeCount}
              </text>
              <text x={60} y={72} textAnchor="middle" fill="#6B7280" fontSize={12}>
                / {totalShakes}
              </text>
            </svg>
            <p style={{ color: "#6B7280", fontSize: 13, marginTop: 8 }}>
              {shakeCount === 0 && "请诚心点击下方按钮，共六次"}
              {shakeCount > 0 && shakeCount < totalShakes && `再摇 ${totalShakes - shakeCount} 次`}
              {shakeCount >= totalShakes && "卦象已定，正在起卦…"}
            </p>
          </div>

          {/* 摇卦按钮 */}
          <div style={{ textAlign: "center", marginBottom: 28 }}>
            <button
              onClick={handleShake}
              disabled={shakeCount >= totalShakes}
              style={{
                ...styles.shakeBtn,
                transform: shakeCount >= totalShakes ? "scale(0.95)" : "scale(1)",
                opacity: shakeCount >= totalShakes ? 0.6 : 1,
              }}
              key={rippleKey}
            >
              <span style={{ fontSize: 32, display: "block", marginBottom: 4 }}>☯</span>
              {shakeCount === 0 ? "摇卦" : shakeCount >= totalShakes ? "完成" : "再摇"}
            </button>
          </div>

          {/* 已摇次数可视化 */}
          <div style={{ display: "flex", justifyContent: "center", gap: 8 }}>
            {Array.from({ length: totalShakes }).map((_, i) => (
              <div
                key={i}
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  background: i < shakeCount ? "#D4AF37" : "rgba(0,0,0,0.12)",
                  transition: "background 0.3s",
                }}
              />
            ))}
          </div>

          {error && (
            <div style={{ ...styles.errorBox, marginTop: 20 }}>{error}</div>
          )}

          <button onClick={reset} style={styles.btnGhost}>
            ← 重新输入占问
          </button>
        </div>
      </div>
    );
  }

  // ── 加载中 ──────────────────────────────────────────────────────────────────
  if (step === "loading") {
    return (
      <div style={{ ...styles.page, justifyContent: "center", alignItems: "center" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 48, marginBottom: 16, animation: "spin 2s linear infinite" }}>
            ☯
          </div>
          <p style={{ color: "#064E3B", fontSize: 14 }}>正在推算卦象，请稍候…</p>
          <style>{`@keyframes spin { from { transform:rotate(0deg); } to { transform:rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  // ── 卦象结果界面 ────────────────────────────────────────────────────────────
  if (step === "result" && result) {
    const movingLine = result.lines?.[result.moving_line - 1];

    return (
      <div style={styles.page}>
        <div style={{ ...styles.container, maxWidth: 680 }}>
          {/* 标题 */}
          <div style={{ textAlign: "center", marginBottom: 24 }}>
            <h2 style={{ fontSize: 20, color: "#064E3B", margin: 0 }}>卦象解读</h2>
            <p style={{ color: "#6B7280", fontSize: 12, marginTop: 4 }}>
              {new Date(result.cast_time).toLocaleString("zh-CN")}
            </p>
          </div>

          {/* 占问 */}
          <div style={{ ...styles.infoBox, marginBottom: 20 }}>
            <span style={{ color: "#6B7280", fontSize: 12 }}>占问：</span>
            <span style={{ color: "#1F2937", fontSize: 14 }}>{result.question}</span>
          </div>

          {/* Phase 120: 卦象元数据 (质量/自代占/再占提示) */}
          {(result.cast_quality || result.cast_mode || result.is_recast) && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
              {/* 自占/代占 */}
              {result.cast_mode && (
                <span style={badgeStyle(
                  result.cast_mode === "self" ? "#064E3B" : "#6B7280"
                )}>
                  {result.cast_mode === "self" ? "自占 · 信息完整" : "匿名 · 信息有限"}
                </span>
              )}
              {/* 卦象质量 */}
              {result.cast_quality && result.cast_quality !== "good" && (
                <span style={badgeStyle(
                  result.cast_quality === "poor" ? "#DC2626" : "#D97706"
                )}>
                  {result.cast_quality === "poor" ? "卦象不稳" : "信号偏弱"}{" "}
                  {result.cast_quality_suggestion && (
                    <span style={{ fontSize: 11, opacity: 0.85 }}>
                      — {result.cast_quality_suggestion}
                    </span>
                  )}
                </span>
              )}
              {/* 初筮告 */}
              {result.is_recast && (
                <span style={badgeStyle("#7C3AED")}>
                  再占 · {result.recast_note}
                </span>
              )}
            </div>
          )}

          {/* 本卦 + 变卦 */}
          <div style={{ display: "flex", gap: 12, marginBottom: 20 }}>
            <HexagramCard
              title={result.hexagram_zh}
              titleEn={result.hexagram_en}
              meaning={result.hexagram_meaning_zh}
              modernTranslation={guaTranslations[result.hexagram_zh]}
              highlight={false}
            />
            <div style={{
              display: "flex",
              alignItems: "center",
              fontSize: 20,
              color: "#8B6914",
              flexShrink: 0,
            }}>
              →
            </div>
            <HexagramCard
              title={result.changed_hex_zh}
              titleEn={result.changed_hex_en}
              meaning={result.changed_meaning_zh}
              modernTranslation={guaTranslations[result.changed_hex_zh]}
              highlight={true}
            />
          </div>

          {/* 动爻标注 */}
          {movingLine && (
            <div style={{
              background: "rgba(251,191,36,0.08)",
              border: "1px solid rgba(251,191,36,0.3)",
              borderRadius: 10,
              padding: "10px 14px",
              marginBottom: 20,
              fontSize: 13,
              color: "#8B6914",
            }}>
              <strong>动爻 第{result.moving_line}爻：</strong>
              {movingLine.branch}({movingLine.element}) · {movingLine.six_rel} · {movingLine.spirit}（{movingLine.spirit_nature}）· {movingLine.wangxiu}
            </div>
          )}

          {/* 六爻详情 */}
          <div style={{
            background: "rgba(0,0,0,0.03)",
            borderRadius: 10,
            padding: "12px 14px",
            marginBottom: 20,
            border: "1px solid rgba(0,0,0,0.06)",
          }}>
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              marginBottom: 10,
              fontSize: 13,
              color: "#6B7280",
              fontWeight: 600,
            }}>
              六爻详解
              <span style={{ fontSize: 11, fontWeight: 400 }}>（初爻→上爻）</span>
            </div>
            {/* 从上爻到初爻显示（传统卦象上重下轻） */}
            {[...result.lines].reverse().map((line, idx) => (
              <YaoLine key={line.position} line={line} idx={5 - idx} />
            ))}
          </div>

          {/* AI 解读 */}
          {result.ai_interpretation_zh && (
            <div style={{
              background: "linear-gradient(135deg,rgba(16,185,129,0.06),rgba(139,92,246,0.06))",
              border: "1px solid rgba(16,185,129,0.2)",
              borderRadius: 12,
              padding: "16px 18px",
              marginBottom: 24,
            }}>
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                marginBottom: 10,
                fontSize: 13,
                color: "#064E3B",
                fontWeight: 700,
              }}>
                ✦ AI 卦象解读
              </div>
              <p style={{
                fontSize: 13,
                color: "#4B5563",
                lineHeight: 1.8,
                margin: 0,
                whiteSpace: "pre-wrap",
              }}>
                {result.ai_interpretation_zh}
              </p>
            </div>
          )}

          {/* ── 大运·流年联动块 ────────────────────────────────── */}
          {cycle && cycle.has_report && (
            <div style={{
              background: "linear-gradient(135deg,rgba(251,191,36,0.06),rgba(139,92,246,0.06))",
              border: "1px solid rgba(251,191,36,0.2)",
              borderRadius: 12,
              padding: "14px 16px",
              marginBottom: 20,
            }}>
              <div style={{ fontSize: 11, color: "#8B6914", fontWeight: 700, letterSpacing: ".1em", marginBottom: 10 }}>
                ◈ 卦象与你的大运能量
              </div>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                {/* 大运 */}
                {cycle.dayun_gz && (
                  <div style={{ background: "rgba(0,0,0,0.05)", borderRadius: 8, padding: "8px 12px", flex: "1 1 100px" }}>
                    <div style={{ fontSize: 10, color: "#6B7280", marginBottom: 3 }}>当前大运</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: "#8B6914" }}>{cycle.dayun_gz}</div>
                  </div>
                )}
                {/* 流年分 */}
                {cycle.year_score != null && (
                  <div style={{ background: "rgba(0,0,0,0.05)", borderRadius: 8, padding: "8px 12px", flex: "1 1 100px" }}>
                    <div style={{ fontSize: 10, color: "#6B7280", marginBottom: 3 }}>2026 流年能量</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: cycle.is_high_energy ? "#10b981" : "#f59e0b" }}>
                      {cycle.year_score}分 · {cycle.year_label || ""}
                    </div>
                  </div>
                )}
                {/* 本尊 */}
                {cycle.top_deity && (
                  <div style={{ background: "rgba(0,0,0,0.05)", borderRadius: 8, padding: "8px 12px", flex: "1 1 100px" }}>
                    <div style={{ fontSize: 10, color: "#6B7280", marginBottom: 3 }}>当前护法</div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: "#a78bfa" }}>{cycle.top_deity}</div>
                  </div>
                )}
              </div>
              <div style={{ fontSize: 11, color: "#64748b", marginTop: 10, lineHeight: 1.6 }}>
                结合此卦象与你当前大运能量，可获更精准解读。
              </div>
            </div>
          )}

          {/* 操作按钮 */}
          <div style={{ display: "flex", gap: 12 }}>
            <button onClick={reset} style={{ ...styles.btnPrimary, flex: 1 }}>
              再占一卦
            </button>
            <button
              onClick={downloadPdf}
              disabled={pdfLoading}
              style={{
                flex: 1,
                padding: "12px 20px",
                borderRadius: 10,
                border: "1px solid rgba(139,92,246,0.5)",
                background: pdfLoading
                  ? "rgba(139,92,246,0.1)"
                  : "rgba(139,92,246,0.15)",
                color: pdfLoading ? "#6B7280" : "#c4b5fd",
                fontSize: 14,
                fontWeight: 600,
                cursor: pdfLoading ? "not-allowed" : "pointer",
                transition: "all 0.2s",
              }}
            >
              {pdfLoading ? "生成中…" : "↓ 下载六爻报告"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}

// ── 辅助 ───────────────────────────────────────────────────────────────────────
function badgeStyle(bgColor) {
  return {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "4px 10px",
    borderRadius: 6,
    background: bgColor + "14",
    border: "1px solid " + bgColor + "33",
    color: bgColor,
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 1.4,
    maxWidth: 360,
  };
}

// ── 样式 ───────────────────────────────────────────────────────────────────────
const styles = {
  page: {
    minHeight: "100vh",
    background: "#F9F7F2",
    display: "flex",
    justifyContent: "center",
    padding: "32px 16px 60px",
    fontFamily: "'PingFang SC','Microsoft YaHei',sans-serif",
  },
  container: {
    width: "100%",
    maxWidth: 520,
  },
  infoBox: {
    background: "rgba(0,0,0,0.04)",
    borderRadius: 10,
    padding: "12px 16px",
    marginBottom: 20,
    border: "1px solid rgba(0,0,0,0.06)",
  },
  textarea: {
    width: "100%",
    boxSizing: "border-box",
    background: "rgba(0,0,0,0.05)",
    border: "1px solid rgba(0,0,0,0.12)",
    borderRadius: 8,
    padding: "10px 12px",
    color: "#1F2937",
    fontSize: 14,
    resize: "vertical",
    outline: "none",
    fontFamily: "inherit",
  },
  btnPrimary: {
    width: "100%",
    padding: "13px 0",
    background: "linear-gradient(135deg,#D4AF37,#8B6914)",
    color: "#fff",
    border: "none",
    borderRadius: 10,
    fontSize: 15,
    fontWeight: 700,
    cursor: "pointer",
    fontFamily: "inherit",
  },
  btnGhost: {
    width: "100%",
    padding: "10px 0",
    background: "transparent",
    color: "#6b7280",
    border: "1px solid rgba(0,0,0,0.08)",
    borderRadius: 10,
    fontSize: 13,
    cursor: "pointer",
    marginTop: 12,
    fontFamily: "inherit",
  },
  shakeBtn: {
    width: 130,
    height: 130,
    borderRadius: "50%",
    background: "linear-gradient(135deg,rgba(212,175,55,0.12),rgba(139,92,246,0.12))",
    border: "2px solid rgba(212,175,55,0.35)",
    color: "#D4AF37",
    fontSize: 14,
    fontWeight: 700,
    cursor: "pointer",
    display: "inline-flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.15s ease",
    fontFamily: "inherit",
    boxShadow: "0 0 20px rgba(212,175,55,0.18)",
  },
  errorBox: {
    background: "rgba(239,68,68,0.1)",
    border: "1px solid rgba(239,68,68,0.3)",
    borderRadius: 8,
    padding: "10px 14px",
    color: "#dc2626",
    fontSize: 13,
    marginBottom: 16,
  },
};
